import { RefObject, useLayoutEffect } from "react";

/**
 * Keeps a textarea exactly as tall as its text, so every line shows and no
 * scrollbar appears. It never gets shorter than its `rows`, and it grows as
 * the person types. Give the textarea `resize-none overflow-hidden`.
 *
 * It measures after every render, and again when the window is resized,
 * since a narrower box wraps the same text onto more lines.
 */
export function useAutoGrowTextarea(ref: RefObject<HTMLTextAreaElement | null>) {
  useLayoutEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;
    const fit = () => {
      // "auto" first, so the box can also shrink when lines are removed.
      textarea.style.height = "auto";
      // scrollHeight leaves out the border, and the box is border-box.
      const border = textarea.offsetHeight - textarea.clientHeight;
      textarea.style.height = `${textarea.scrollHeight + border}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  });
}
